<template>
  <div class="create-job-profile-step2 d-flex flex-column h-100">
    <div class="step-header pa-6 pb-4">
      <div class="d-flex align-center ga-3">
        <div>
          <h1 class="text-h1 text-textDarkest">
            Choose a Template
          </h1>
          <div class="text-body-2 text-textLight mt-1">
            Select an optional template to pre-fill your new profile's content.
          </div>
        </div>
      </div>
    </div>

    <v-divider class="opacity-100" />

    <div class="step-body flex-grow-1 overflow-hidden">
      <v-row
        no-gutters
        class="h-100"
      >
        <v-col
          cols="5"
          class="border-e d-flex flex-column h-100"
        >
          <div
            id="templates-heading"
            class="pa-4 pb-2 text-caption text-textDark text-uppercase font-weight-medium"
          >
            Templates
          </div>
          <div
            v-if="store.templatesLoading"
            class="d-flex justify-center pa-6"
          >
            <v-progress-circular
              indeterminate
              color="brandPrimary"
              size="32"
              width="2"
            />
          </div>

          <div
            v-else-if="store.templates.length === 0"
            class="text-body-2 text-textLight text-center pa-6"
          >
            No templates available. You can still create a blank profile.
          </div>

          <ul
            v-else
            class="template-list pa-2 overflow-y-auto flex-grow-1"
            aria-labelledby="templates-heading"
          >
            <li
              class="template-list-item rounded-lg"
              :class="{ selected: store.selectedTemplateUlid === null }"
            >
              <button
                type="button"
                class="pa-3 w-100 text-left"
                :aria-current="store.selectedTemplateUlid === null ? 'true' : undefined"
                @click="clearTemplate"
              >
                <div class="text-h3 text-textDarkest">
                  No template
                </div>
                <div class="text-body-3 text-textLight opacity-100">
                  Start with a blank profile
                </div>
              </button>
            </li>

            <li
              v-for="template in store.templates"
              :key="template.ulid"
              class="template-list-item rounded-lg"
              :class="{ selected: store.selectedTemplateUlid === template.ulid }"
            >
              <button
                type="button"
                class="pa-3 w-100 text-left"
                :aria-current="store.selectedTemplateUlid === template.ulid ? 'true' : undefined"
                @click="store.selectTemplate(template.ulid)"
              >
                <div class="text-h3 text-textDarkest">
                  {{ template.title }}
                </div>
                <div
                  v-if="template.memo"
                  class="text-body-3 text-textLight opacity-100"
                >
                  {{ template.memo }}
                </div>
              </button>
            </li>
          </ul>
        </v-col>

        <v-col
          cols="7"
          class="overflow-y-auto h-100"
        >
          <WizardProfilePreview
            :profile="store.mergedProfileAndTemplatePreview ? {
              ...store.mergedProfileAndTemplatePreview,
              title: formTitle,
              job_code: formJobCode,
              location: formLocation,
              is_hris_profile: false,
            } : null"
            :loading="store.templatePreviewLoading"
          >
            <template #empty>
              Select a template on the left to preview its content here.
            </template>
          </WizardProfilePreview>
        </v-col>
      </v-row>
    </div>

    <v-divider class="opacity-100" />

    <div class="step-footer pa-6 d-flex ga-4">
      <v-btn
        class="h-100"
        variant="text"
        color="textDarkest"
        density="compact"
        prepend-icon="$arrow-left"
        @click="store.goToStep1"
      >
        Back
      </v-btn>

      <v-btn
        color="primary"
        variant="flat"
        rounded="lg"
        :loading="store.createLoading"
        @click="store.createProfile(router)"
      >
        Create Profile
      </v-btn>
    </div>
  </div>
</template>

<script setup>
import { onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useCreateJobProfileWizard } from '@/stores/jobProfiles/createJobProfileWizard';
import { storeToRefs } from 'pinia';
import WizardProfilePreview from './WizardProfilePreview.vue';

const store = useCreateJobProfileWizard();
const router = useRouter();
const { formTitle, formJobCode, formLocation } = storeToRefs(store);

const clearTemplate = () => {
  store.selectedTemplateUlid = null;
  store.templatePreview = null;
};

onMounted(() => {
  store.loadTemplates();
});
</script>

<style lang="less" scoped>
.template-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.template-list-item {
  border: 1px solid transparent;

  &.selected {
    border-color: @stroke-default;
  }

  &:hover {
    background-color: @bg-canvas;
  }
}
</style>
